import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getRequestData } from 'api/api';
import { endpoints } from 'api/endpoints';
import {
  SelectOption,
  IconPlus,
  OrangeButton,
  IconPen,
  IconFilter,
  IconSmallArrow,
  CustomTable,
  DownloadClientsCSV,
  AddEditPFAdminModal,
  IconSearch,
  SmallButton,
  IconReset,
  IconEye,
} from 'components';
import { COLORS } from 'utils/colors';
import { PF25_ADMIN_STATUS, ROLE_TYPES } from 'api/constants';
import { Input } from 'antd';
import DefaultAvatar from 'assets/images/default-avatar.png';
import { useUser } from 'context/userContext';
import { useNavigate } from 'react-router-dom';

export const PF25Admins = () => {
  const { userDetails } = useUser();
  const navigate = useNavigate();
  const [admins, setAdmins] = useState();
  const [searchWord, setSearchWord] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    gender: '',
  });

  const fetchAdmins = useCallback(async (params) => {
    const { data } = await getRequestData(endpoints.getPF25Admins(params), ROLE_TYPES.admin);
    if (data) setAdmins(data);
  }, []);

  useEffect(() => {
    fetchAdmins({ page: 1 });
  }, [fetchAdmins]);

  const handleSearch = (e) => {
    const searchTerm = e.target.value;

    if (searchTerm.length > 2 || (searchWord && searchWord.length > searchTerm.length)) {
      fetchAdminsWithFilters(selectedStatus, searchTerm);
    }
    setSearchWord(searchTerm);
  };

  const handleStatusSelectChange = (status) => {
    setSelectedStatus(status);
    fetchAdminsWithFilters(status, searchWord);
  };

  const fetchAdminsWithFilters = (status, searchQuery, page = 1) => {
    const filters = {};
    if (status) filters.status = status;
    if (searchQuery) filters.search = searchQuery;
    if (page) filters.page = page;
    fetchAdmins(filters);
  };

  const handleFiltersReset = useCallback(() => {
    setSelectedStatus(null);
    setSearchWord(null);
    fetchAdmins({ page: 1 });
  }, [fetchAdmins]);

  const handlePaginationChange = (page) => {
    const filters = { page };

    if (selectedStatus) {
      filters.status = selectedStatus;
    }

    if (searchWord) {
      filters.search = searchWord;
    }

    fetchAdmins(filters);
  };

  const storeBtn = useMemo(() => {
    if (selectedStatus || searchWord) {
      return <SmallButton onClick={handleFiltersReset} label="Reset All" icon={<IconReset />} />;
    }
    return null;
  }, [handleFiltersReset, searchWord, selectedStatus]);

  const columns = [
    {
      title: 'Admin',
      dataIndex: 'admin',
      width: 350,
      render: (_, record) => (
        <div
          className={`flex items-center gap-x-1 ${record.status === PF25_ADMIN_STATUS.inactive ? 'pointer-events-none opacity-50' : ''}`}
        >
          <img
            className="w-[30px] h-[30px] rounded-full"
            src={record.avatar ? record.avatar : DefaultAvatar}
            alt="Resident"
          />
          <div className="flex flex-col ml-2">
            <p className="text-14 mb-0.5">
              {record.firstname} {record.lastname}
            </p>
            <p className="text-12 text-lightBlue font-normal">{record.email}</p>
          </div>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'role',
      render: (role) => (
        <p className="text-14 font-medium overflow-hidden whitespace-nowrap pr-4 capitalize">
          {role}
        </p>
      ),
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      render: (_, record) => (
        <p className="text-14 font-medium overflow-hidden whitespace-nowrap pr-4">
          {record.country_code}
          {record.phone}
        </p>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status) => (
        <p className="text-14 capitalize font-medium overflow-hidden">{status}</p>
      ),
    },
    {
      title: 'Action',
      width: 50,
      dataIndex: 'id',
      render: (_, record) => (
        <div className="flex items-center gap-x-2">
          <IconEye
            color="#4318FF"
            className={`w-8 h-8 p-1 ml-[auto] bg-light rounded-[8px] cursor-pointer`}
            onClick={() => navigate(`/admin-profile/${record.id}`)}
          />
          <IconPen
            color="#4318FF"
            className={`w-8 h-8 p-1 ml-[auto] bg-light rounded-[8px] cursor-pointer ${record.id === userDetails?.user_id ? 'pointer-events-none opacity-50' : ''}`}
            onClick={() => {
              setIsModalOpen(true);
              setIsEditMode(true);
              setFormData(record);
            }}
          />
        </div>
      ),
    },
  ];

  return (
    admins && (
      <div className="flex flex-col w-full gap-y-6">
        <div className="flex justify-between w-full">
          <p className="nunito-title">PF25 Admins</p>
          <div className="flex items-center gap-x-6">
            <Input
              className="search w-[300px] text-lightBlue text-16 rounded-ten bg-light"
              type="text"
              value={searchWord}
              placeholder="Search"
              addonBefore={<IconSearch color={COLORS.lightBlue} />}
              onChange={handleSearch}
            />
            <OrangeButton
              onClick={() => setIsModalOpen(true)}
              label="Add new admin"
              icon={<IconPlus />}
            />
            <DownloadClientsCSV list={admins.results} />
          </div>
        </div>
        {
          <>
            <div className="flex items-center gap-x-3">
              <div className="flex items-center gap-x-1 mr-3">
                <IconFilter />
                <p className="text-16">Filter</p>
              </div>
              <SelectOption
                onSelect={(value) => handleStatusSelectChange(value)}
                dropdownStyle={{ padding: '16px', background: 'white' }}
                required
                value={selectedStatus}
                suffixIcon={
                  <IconSmallArrow color={COLORS.black} className="rotate-[-90deg] w-3 h-3" />
                }
                className="h-[40px] text-base font-medium base-block rounded-lg"
                popupClassName="min-w-[180px]"
                placeholder="Status"
                dropdownRender={(originalNode) => originalNode}
                optionRender={(option) => (
                  <div
                    className={`flex items-center px-2 py-1 gap-x-1 text-black rounded-md capitalize border border-color-divider bg-white overflow-hidden ${selectedStatus === option.value ? 'border-orange' : ''}`}
                  >
                    <span
                      className={`w-6 h-6  rounded-full border border-orange ${selectedStatus === option.value ? 'border-[6px]' : ''}`}
                    />
                    <p className="font-14 flex-1">{option.label}</p>
                  </div>
                )}
                options={[
                  {
                    label: <p className="text-14 px-4 py-1.5 rounded-md">Active</p>,
                    value: PF25_ADMIN_STATUS.active,
                  },
                  {
                    label: <p className="text-14 px-4 py-1.5 rounded-md">Inactive</p>,
                    value: PF25_ADMIN_STATUS.inactive,
                  },
                  {
                    label: <p className="text-14 px-4 py-1.5 rounded-md">Pending</p>,
                    value: PF25_ADMIN_STATUS.pending,
                  },
                ]}
              />
              {storeBtn}
            </div>
            <CustomTable
              columns={columns}
              data={admins.results}
              onChange={handlePaginationChange}
              rowKey={(record) => record?.id}
              pageCount={admins.pageCount}
              current={admins.currentPage}
              pageSize={admins.pageSize}
            />
          </>
        }
        {isModalOpen &&
          (isEditMode ? (
            <AddEditPFAdminModal
              onSuccess={() => fetchAdmins({ page: 1 })}
              isOpen={isModalOpen}
              onClose={() => {
                setIsModalOpen(false);
                setIsEditMode(false);
              }}
              adminData={formData}
              isEditMode={isEditMode}
              role={ROLE_TYPES.admin}
            />
          ) : (
            <AddEditPFAdminModal
              onSuccess={() => fetchAdmins({ page: 1 })}
              isOpen={isModalOpen}
              role={ROLE_TYPES.admin}
              onClose={() => setIsModalOpen(false)}
            />
          ))}
      </div>
    )
  );
};
